import {
  BookingGeneralState,
  BookingMirState,
  BookingPaymentState,
} from '@core/config/booking-states.config';
import type {
  Booking,
  BookingDailyPrice,
  BookingHolder,
  BookingMainGuest,
  BookingPayment,
  BookingPhone,
  BookingStateHistory,
  BookingTime,
} from '@interfaces/booking.interface';

import {
  ACTIVE_STATUSES,
  BOOKING_CHANNELS,
  BOOKING_STATUSES,
  BOOKING_TYPES,
  CANCELLATION_POLICIES,
  CANCELLED_STATUSES,
  CHECK_IN_STATUSES,
  CONFIRMED_STATUSES,
  CURRENCIES,
  ERROR_STATUSES,
  FINISHED_STATUSES,
  FIRST_NAMES,
  LAST_NAMES,
  LOCALES,
  PAYMENT_GATEWAYS,
  PENDING_STATUSES,
} from '../pools';
import {
  daysFromNow,
  isoNow,
  pick,
  randomBool,
  randomFloat,
  randomInt,
  randomPhone,
  seq,
  uuid,
} from '../utils';

type StatusGroup = 'active' | 'pending' | 'confirmed' | 'finished' | 'cancelled' | 'error';

function classifyStatus(status: string): StatusGroup {
  if ((ACTIVE_STATUSES as readonly string[]).includes(status)) {
    return 'active';
  }
  if ((PENDING_STATUSES as readonly string[]).includes(status)) {
    return 'pending';
  }
  if ((CONFIRMED_STATUSES as readonly string[]).includes(status)) {
    return 'confirmed';
  }
  if ((FINISHED_STATUSES as readonly string[]).includes(status)) {
    return 'finished';
  }
  if ((CANCELLED_STATUSES as readonly string[]).includes(status)) {
    return 'cancelled';
  }
  return 'error';
}

function buildBookingTime(status: string): BookingTime {
  const group = classifyStatus(status);
  let checkInOffset: number;
  let stayLength: number;

  switch (group) {
    case 'active':
      checkInOffset = randomInt(-3, 0);
      stayLength = randomInt(3, 10);
      break;
    case 'pending':
    case 'confirmed':
      checkInOffset = randomInt(5, 60);
      stayLength = randomInt(2, 14);
      break;
    case 'finished':
      checkInOffset = randomInt(-90, -10);
      stayLength = randomInt(2, 10);
      break;
    case 'cancelled':
      checkInOffset = randomInt(-30, 30);
      stayLength = randomInt(2, 7);
      break;
    case 'error':
    default:
      checkInOffset = randomInt(-60, -5);
      stayLength = randomInt(2, 7);
      break;
  }

  return {
    checkInDate: daysFromNow(checkInOffset),
    checkOutDate: daysFromNow(checkInOffset + stayLength),
    checkInTime: '15:00',
    checkOutTime: '11:00',
  };
}

function buildPhone(): BookingPhone {
  return randomPhone();
}

function buildHolder(): BookingHolder {
  return {
    name: pick(FIRST_NAMES),
    lastName: pick(LAST_NAMES),
    email: `${pick(FIRST_NAMES).toLowerCase()}.${pick(LAST_NAMES).toLowerCase()}@example.com`,
    phone: buildPhone(),
  };
}

function buildMainGuest(): BookingMainGuest {
  const first = pick(FIRST_NAMES);
  const last = pick(LAST_NAMES);
  return {
    name: first,
    surname: last,
    lastName: last,
    email: `${first.toLowerCase()}.${last.toLowerCase()}@mock.dev`,
  };
}

function buildDailyBreakdown(
  checkIn: string,
  checkOut: string,
  avgPrice: number
): BookingDailyPrice[] {
  const start = new Date(checkIn);
  const end = new Date(checkOut);
  const prices: BookingDailyPrice[] = [];
  const d = new Date(start);
  while (d < end) {
    prices.push({
      date: d.toISOString().slice(0, 10),
      price: randomFloat(avgPrice * 0.8, avgPrice * 1.2, 2),
    });
    d.setDate(d.getDate() + 1);
  }
  return prices;
}

function buildPayment(totalPrice: number, currency: string): BookingPayment {
  return {
    amount: totalPrice,
    paymentCurrency: currency,
    gateway: pick(PAYMENT_GATEWAYS),
    method: 'card',
    paymentStatus: 'PAID',
    transactionId: `txn-${uuid().slice(0, 8)}`,
    conversionRate: 1.0,
    amountOriginalCurrency: totalPrice,
    feeAmount: randomFloat(1, 15, 2),
    createdAt: isoNow(-randomInt(1, 30)),
  };
}

const STATUS_HISTORY_PATHS: Record<string, string[]> = {
  ACCEPTANCE_PENDING: ['ACCEPTANCE_PENDING'],
  ACCEPTANCE_CANCELED: ['ACCEPTANCE_PENDING', 'ACCEPTANCE_CANCELED'],
  ACCEPTANCE_CANCELLED: ['ACCEPTANCE_PENDING', 'ACCEPTANCE_CANCELLED'],
  ACCEPTANCE_TIMEOUT: ['ACCEPTANCE_PENDING', 'ACCEPTANCE_TIMEOUT'],
  PAYMENT_PENDING: ['ACCEPTANCE_PENDING', 'PAYMENT_PENDING'],
  SUSPENDED_PAYMENT_PENDING: ['ACCEPTANCE_PENDING', 'PAYMENT_PENDING', 'SUSPENDED_PAYMENT_PENDING'],
  PAYMENT_TIMEOUT: ['ACCEPTANCE_PENDING', 'PAYMENT_PENDING', 'PAYMENT_TIMEOUT'],
  GUEST_MIR_DATA_PENDING: ['ACCEPTANCE_PENDING', 'PAYMENT_PENDING', 'GUEST_MIR_DATA_PENDING'],
  SEND_GUEST_MIR_DATA_PENDING: [
    'ACCEPTANCE_PENDING',
    'PAYMENT_PENDING',
    'GUEST_MIR_DATA_PENDING',
    'SEND_GUEST_MIR_DATA_PENDING',
  ],
  SUSPENDED_GUEST_MIR_DATA_PENDING: [
    'ACCEPTANCE_PENDING',
    'PAYMENT_PENDING',
    'GUEST_MIR_DATA_PENDING',
    'SUSPENDED_GUEST_MIR_DATA_PENDING',
  ],
  GUEST_MIR_DATA_TIMEOUT: [
    'ACCEPTANCE_PENDING',
    'PAYMENT_PENDING',
    'GUEST_MIR_DATA_PENDING',
    'GUEST_MIR_DATA_TIMEOUT',
  ],
  BOOKING_NOTIFICATION_PENDING: [
    'ACCEPTANCE_PENDING',
    'PAYMENT_PENDING',
    'GUEST_MIR_DATA_PENDING',
    'BOOKING_NOTIFICATION_PENDING',
  ],
  BOOKING_NOTIFICATION_ERROR: [
    'ACCEPTANCE_PENDING',
    'PAYMENT_PENDING',
    'GUEST_MIR_DATA_PENDING',
    'BOOKING_NOTIFICATION_ERROR',
  ],
  SUSPENDED_BOOKING_NOTIFICATION_ERROR: [
    'ACCEPTANCE_PENDING',
    'PAYMENT_PENDING',
    'GUEST_MIR_DATA_PENDING',
    'BOOKING_NOTIFICATION_ERROR',
    'SUSPENDED_BOOKING_NOTIFICATION_ERROR',
  ],
  BOOKING_NOTIFICATION_TIMEOUT: [
    'ACCEPTANCE_PENDING',
    'PAYMENT_PENDING',
    'GUEST_MIR_DATA_PENDING',
    'BOOKING_NOTIFICATION_PENDING',
    'BOOKING_NOTIFICATION_TIMEOUT',
  ],
  CHECKIN_PENDING: [
    'ACCEPTANCE_PENDING',
    'PAYMENT_PENDING',
    'GUEST_MIR_DATA_PENDING',
    'BOOKING_NOTIFICATION_PENDING',
    'CHECKIN_PENDING',
  ],
  CHECKIN_ERROR: [
    'ACCEPTANCE_PENDING',
    'PAYMENT_PENDING',
    'GUEST_MIR_DATA_PENDING',
    'BOOKING_NOTIFICATION_PENDING',
    'CHECKIN_ERROR',
  ],
  CHECKIN_NOTIFICATION_TIMEOUT: [
    'ACCEPTANCE_PENDING',
    'PAYMENT_PENDING',
    'GUEST_MIR_DATA_PENDING',
    'BOOKING_NOTIFICATION_PENDING',
    'CHECKIN_PENDING',
    'CHECKIN_NOTIFICATION_TIMEOUT',
  ],
  CHECKOUT_PENDING: [
    'ACCEPTANCE_PENDING',
    'PAYMENT_PENDING',
    'GUEST_MIR_DATA_PENDING',
    'BOOKING_NOTIFICATION_PENDING',
    'CHECKIN_PENDING',
    'CHECKOUT_PENDING',
  ],
  CHECKOUT_HOST_CONFIRMATION_PENDING: [
    'ACCEPTANCE_PENDING',
    'PAYMENT_PENDING',
    'GUEST_MIR_DATA_PENDING',
    'BOOKING_NOTIFICATION_PENDING',
    'CHECKIN_PENDING',
    'CHECKOUT_PENDING',
    'CHECKOUT_HOST_CONFIRMATION_PENDING',
  ],
  BOOKING_CHECKOUT_ERROR: [
    'ACCEPTANCE_PENDING',
    'PAYMENT_PENDING',
    'GUEST_MIR_DATA_PENDING',
    'BOOKING_NOTIFICATION_PENDING',
    'CHECKIN_PENDING',
    'CHECKOUT_PENDING',
    'BOOKING_CHECKOUT_ERROR',
  ],
  FINISHED: [
    'ACCEPTANCE_PENDING',
    'PAYMENT_PENDING',
    'GUEST_MIR_DATA_PENDING',
    'BOOKING_NOTIFICATION_PENDING',
    'CHECKIN_PENDING',
    'CHECKOUT_PENDING',
    'CHECKOUT_HOST_CONFIRMATION_PENDING',
    'FINISHED',
  ],
  BOOKING_CANCELLED: ['ACCEPTANCE_PENDING', 'BOOKING_CANCELLED'],
};

export function buildBookingStateHistory(
  currentStatus: string,
  createdDate: string
): BookingStateHistory[] {
  const path = STATUS_HISTORY_PATHS[currentStatus] ?? [currentStatus];
  const start = new Date(createdDate).getTime();
  const stepMs = 60 * 60 * 1000;

  return path.map((state, index) => ({
    state,
    changedAt: new Date(start + index * stepMs).toISOString(),
  }));
}

/** The three parallel machine states a booking carries in the delivery's model. */
export interface BookingStateMachines {
  /** `BookingWorkflow.General`, rendered in the Reserva column (`booking.bookingStatus`). */
  bookingStatus: string;
  /** `BookingWorkflow.Payment`, rendered in the Pagos column; null leaves the cell empty. */
  paymentState: string | null;
  /** `BookingWorkflow.Mir`, rendered in the Hospederías column; null leaves the cell empty. */
  mirState: string | null;
}

/**
 * Splits a legacy single-workflow status (`BO_Estados_Booking` v4 and the shared status matrix the
 * backend still answers Home with) into the three-machine model the bookings list, the header and
 * the calendar detail read (`BO_Estados_Booking_Tres_Maquinas` v5). The old codes packed the whole
 * lifecycle into `bookingStatus`; here each one lands in the machine that owns it so no mock booking
 * resolves to «Desconocido» in those surfaces. A code already in the General machine passes through.
 * `stateHistory` keeps the original legacy codes: the timeline reads them off the shared matrix.
 */
const LEGACY_BOOKING_STATE_MACHINES: Record<string, BookingStateMachines> = {
  // Acceptance — payment and registration have not begun.
  START: {
    bookingStatus: BookingGeneralState.START,
    paymentState: BookingPaymentState.PAYMENT_NOT_STARTED,
    mirState: BookingMirState.MIR_START,
  },
  INSTANT_CHOICE: {
    bookingStatus: BookingGeneralState.INSTANT_CHOICE,
    paymentState: BookingPaymentState.PAYMENT_NOT_STARTED,
    mirState: BookingMirState.MIR_START,
  },
  ACCEPTANCE_PENDING: {
    bookingStatus: BookingGeneralState.ACCEPTANCE_PENDING,
    paymentState: BookingPaymentState.PAYMENT_NOT_STARTED,
    mirState: BookingMirState.MIR_START,
  },
  ACCEPTANCE_TIMEOUT: {
    bookingStatus: BookingGeneralState.ACCEPTANCE_TIMEOUT,
    paymentState: BookingPaymentState.PAYMENT_NOT_STARTED,
    mirState: BookingMirState.MIR_START,
  },
  ACCEPTANCE_CANCELLED: {
    bookingStatus: BookingGeneralState.ACCEPTANCE_CANCELLED,
    paymentState: BookingPaymentState.PAYMENT_NOT_STARTED,
    mirState: BookingMirState.MIR_START,
  },
  ACCEPTANCE_CANCELED: {
    bookingStatus: BookingGeneralState.ACCEPTANCE_CANCELLED,
    paymentState: BookingPaymentState.PAYMENT_NOT_STARTED,
    mirState: BookingMirState.MIR_START,
  },

  // Accepted, payment pending — the booking is confirmed (checkin scheduled).
  PAYMENT_PENDING: {
    bookingStatus: BookingGeneralState.CHECKIN_SCHEDULED,
    paymentState: BookingPaymentState.PAYMENT_PENDING,
    mirState: BookingMirState.MIR_START,
  },
  SUSPENDED_PAYMENT_PENDING: {
    bookingStatus: BookingGeneralState.CHECKIN_SCHEDULED,
    paymentState: BookingPaymentState.PAYMENT_PENDING,
    mirState: BookingMirState.MIR_START,
  },
  PAYMENT_TIMEOUT: {
    bookingStatus: BookingGeneralState.PAYMENT_TIMEOUT,
    paymentState: BookingPaymentState.PAYMENT_CLOSED,
    mirState: BookingMirState.MIR_START,
  },

  // Paid, guest registration (MIR) in flight.
  GUEST_MIR_DATA_PENDING: {
    bookingStatus: BookingGeneralState.CHECKIN_SCHEDULED,
    paymentState: BookingPaymentState.PAYMENT_PAID,
    mirState: BookingMirState.GUEST_MIR_DATA_PENDING,
  },
  SEND_GUEST_MIR_DATA_PENDING: {
    bookingStatus: BookingGeneralState.CHECKIN_SCHEDULED,
    paymentState: BookingPaymentState.PAYMENT_PAID,
    mirState: BookingMirState.SEND_GUEST_MIR_DATA_PENDING,
  },
  SUSPENDED_GUEST_MIR_DATA_PENDING: {
    bookingStatus: BookingGeneralState.CHECKIN_SCHEDULED,
    paymentState: BookingPaymentState.PAYMENT_PAID,
    mirState: BookingMirState.GUEST_MIR_DATA_PENDING,
  },
  GUEST_MIR_DATA_TIMEOUT: {
    bookingStatus: BookingGeneralState.GUEST_MIR_DATA_TIMEOUT,
    paymentState: BookingPaymentState.PAYMENT_PAID,
    mirState: BookingMirState.GUEST_MIR_DATA_PENDING,
  },

  // Registration sent to the authorities.
  BOOKING_NOTIFICATION_PENDING: {
    bookingStatus: BookingGeneralState.CHECKIN_SCHEDULED,
    paymentState: BookingPaymentState.PAYMENT_PAID,
    mirState: BookingMirState.BOOKING_NOTIFICATION_PENDING,
  },
  BOOKING_NOTIFICATION_ERROR: {
    bookingStatus: BookingGeneralState.CHECKIN_SCHEDULED,
    paymentState: BookingPaymentState.PAYMENT_PAID,
    mirState: BookingMirState.BOOKING_NOTIFICATION_ERROR,
  },
  SUSPENDED_BOOKING_NOTIFICATION_ERROR: {
    bookingStatus: BookingGeneralState.CHECKIN_SCHEDULED,
    paymentState: BookingPaymentState.PAYMENT_PAID,
    mirState: BookingMirState.BOOKING_NOTIFICATION_ERROR,
  },
  BOOKING_NOTIFICATION_TIMEOUT: {
    bookingStatus: BookingGeneralState.BOOKING_NOTIFICATION_TIMEOUT,
    paymentState: BookingPaymentState.PAYMENT_PAID,
    mirState: BookingMirState.BOOKING_NOTIFICATION_PENDING,
  },

  // Guest check-in registration.
  CHECKIN_PENDING: {
    bookingStatus: BookingGeneralState.CHECKIN_SCHEDULED,
    paymentState: BookingPaymentState.PAYMENT_PAID,
    mirState: BookingMirState.CHECKIN_PENDING,
  },
  CHECKIN_ERROR: {
    bookingStatus: BookingGeneralState.CHECKIN_SCHEDULED,
    paymentState: BookingPaymentState.PAYMENT_PAID,
    mirState: BookingMirState.CHECKIN_ERROR,
  },
  CHECKIN_NOTIFICATION_TIMEOUT: {
    bookingStatus: BookingGeneralState.CHECKIN_SCHEDULED,
    paymentState: BookingPaymentState.PAYMENT_PAID,
    mirState: BookingMirState.CHECKIN_NOTIFICATION_PENDING,
  },

  // Checkout.
  CHECKOUT_PENDING: {
    bookingStatus: BookingGeneralState.CHECKOUT_PENDING,
    paymentState: BookingPaymentState.PAYMENT_PAID,
    mirState: BookingMirState.MIR_COMPLETED,
  },
  CHECKOUT_HOST_CONFIRMATION_PENDING: {
    bookingStatus: BookingGeneralState.CHECKOUT_HOST_CONFIRMATION_PENDING,
    paymentState: BookingPaymentState.PAYMENT_PAID,
    mirState: BookingMirState.MIR_COMPLETED,
  },
  BOOKING_CHECKOUT_ERROR: {
    bookingStatus: BookingGeneralState.BOOKING_CHECKOUT_ERROR,
    paymentState: BookingPaymentState.PAYMENT_PAID,
    mirState: BookingMirState.MIR_COMPLETED,
  },

  // Terminal.
  FINISHED: {
    bookingStatus: BookingGeneralState.FINISHED,
    paymentState: BookingPaymentState.PAYMENT_CLOSED,
    mirState: BookingMirState.MIR_COMPLETED,
  },
  BOOKING_CANCELLED: {
    bookingStatus: BookingGeneralState.CANCELLED_BY_HOST,
    paymentState: BookingPaymentState.PAYMENT_CLOSED,
    mirState: BookingMirState.MIR_CANCELLED,
  },

  // Codes already expressed in the General machine (v5), used by the day-0 scenarios.
  CHECKIN_SCHEDULED: {
    bookingStatus: BookingGeneralState.CHECKIN_SCHEDULED,
    paymentState: BookingPaymentState.PAYMENT_PAID,
    mirState: BookingMirState.SEND_GUEST_MIR_DATA_PENDING,
  },
  CANCELLED_BY_GUEST: {
    bookingStatus: BookingGeneralState.CANCELLED_BY_GUEST,
    paymentState: BookingPaymentState.PAYMENT_CLOSED,
    mirState: BookingMirState.MIR_CANCELLED,
  },
  CANCELLED_BY_HOST: {
    bookingStatus: BookingGeneralState.CANCELLED_BY_HOST,
    paymentState: BookingPaymentState.PAYMENT_CLOSED,
    mirState: BookingMirState.MIR_CANCELLED,
  },
  CANCELLED_BY_CHANNEL: {
    bookingStatus: BookingGeneralState.CANCELLED_BY_CHANNEL,
    paymentState: BookingPaymentState.PAYMENT_CLOSED,
    mirState: BookingMirState.MIR_CANCELLED,
  },
};

/**
 * Resolves the three machine states a built booking carries from the lifecycle status a scenario
 * asks for. A status the map does not know (e.g. the retired CHANNEL_CONFIRMED a legacy fixture
 * pins through an override) is left untouched in `bookingStatus` with no payment or MIR state.
 */
export function bookingStateMachinesFor(status: string): BookingStateMachines {
  return (
    LEGACY_BOOKING_STATE_MACHINES[status] ?? {
      bookingStatus: status,
      paymentState: null,
      mirState: null,
    }
  );
}

function pickWeightedStatus(): string {
  const roll = randomInt(1, 100);
  if (roll <= 25) {
    return pick(ACTIVE_STATUSES);
  }
  if (roll <= 45) {
    return pick(PENDING_STATUSES);
  }
  if (roll <= 55) {
    return pick(CONFIRMED_STATUSES);
  }
  if (roll <= 80) {
    return pick(FINISHED_STATUSES);
  }
  if (roll <= 90) {
    return pick(CANCELLED_STATUSES);
  }
  return pick(ERROR_STATUSES);
}

export function buildBooking(
  rentalIds: string[],
  hostId: string,
  overrides?: Partial<Booking>
): Booking {
  const status = overrides?.bookingStatus ?? pickWeightedStatus();
  const time = buildBookingTime(status);
  const avgPrice = randomFloat(80, 350, 2);
  const dailyBreakDown = buildDailyBreakdown(time.checkInDate, time.checkOutDate, avgPrice);
  const totalBeforeAdj = dailyBreakDown.reduce((sum, d) => sum + d.price, 0);
  const taxAmount = randomFloat(totalBeforeAdj * 0.05, totalBeforeAdj * 0.15, 2);
  const totalPrice = parseFloat((totalBeforeAdj + taxAmount).toFixed(2));
  const currency = pick(CURRENCIES);
  const rentalId = pick(rentalIds);
  const createdDate = overrides?.createdDate ?? isoNow(-randomInt(1, 90));
  const stateHistory = buildBookingStateHistory(status, createdDate);
  const updatedDate = stateHistory[stateHistory.length - 1]?.changedAt ?? createdDate;
  const states = bookingStateMachinesFor(status);

  return {
    id: uuid(),
    externalCodeNumber: seq(),
    numberOfGuests: randomInt(1, 8),
    note: randomBool(0.3) ? 'Late arrival expected' : undefined,
    specialRequests: randomBool(0.2) ? 'Extra towels please' : undefined,
    bookingType: pick(BOOKING_TYPES),
    bookingStatus: states.bookingStatus,
    paymentState: states.paymentState,
    mirState: states.mirState,
    stateHistory,
    channel: pick(BOOKING_CHANNELS),
    checkInStatus: pick(CHECK_IN_STATUSES),
    locale: pick(LOCALES),
    createdDate,
    createdBy: 'system',
    updatedDate,
    updatedBy: 'system',
    rentalId,
    hostId,
    cancellationPolicy: pick(CANCELLATION_POLICIES),
    isRefundable: randomBool(0.7),
    averageNightlyPrice: avgPrice,
    dailyBreakDown,
    totalBeforeAdjustements: parseFloat(totalBeforeAdj.toFixed(2)),
    maintenance: randomBool(0.2)
      ? { amount: randomFloat(20, 100, 2), description: 'Cleaning fee' }
      : undefined,
    discount: randomBool(0.3)
      ? { amount: randomFloat(10, 50, 2), description: 'Early bird discount' }
      : undefined,
    taxes: { amount: taxAmount, description: 'Tourist tax' },
    totalPrice,
    priceBreakDownCurrency: currency,
    payments: randomBool(0.7) ? [buildPayment(totalPrice, currency)] : [],
    refunds: [],
    bookingHolder: buildHolder(),
    mainGuest: buildMainGuest(),
    rentalTitle: undefined,
    time,
    fullPhone: buildPhone(),
    defaultPaymentGateway: pick(PAYMENT_GATEWAYS),
    ...overrides,
  };
}

export function buildBookings(count: number, rentalIds: string[], hostId: string): Booking[] {
  return Array.from({ length: count }, () => buildBooking(rentalIds, hostId));
}

/**
 * Build a booking with explicit check-in / check-out date strings (YYYY-MM-DD).
 * All other fields are seeded-random, same as buildBooking().
 */
export function buildBookingWithFixedDates(
  rentalId: string,
  hostId: string,
  checkIn: string,
  checkOut: string,
  status: string,
  channel?: string
): Booking {
  const time: BookingTime = {
    checkInDate: checkIn,
    checkOutDate: checkOut,
    checkInTime: '15:00',
    checkOutTime: '11:00',
  };
  const avgPrice = randomFloat(80, 350, 2);
  const dailyBreakDown = buildDailyBreakdown(checkIn, checkOut, avgPrice);
  const totalBeforeAdj = dailyBreakDown.reduce((sum, d) => sum + d.price, 0);
  const taxAmount = randomFloat(totalBeforeAdj * 0.05, totalBeforeAdj * 0.15, 2);
  const totalPrice = parseFloat((totalBeforeAdj + taxAmount).toFixed(2));
  const currency = pick(CURRENCIES);
  const createdDate = isoNow(-randomInt(1, 90));
  const stateHistory = buildBookingStateHistory(status, createdDate);
  const updatedDate = stateHistory[stateHistory.length - 1]?.changedAt ?? createdDate;
  const states = bookingStateMachinesFor(status);

  return {
    id: uuid(),
    externalCodeNumber: seq(),
    numberOfGuests: randomInt(1, 8),
    note: randomBool(0.3) ? 'Late arrival expected' : undefined,
    specialRequests: randomBool(0.2) ? 'Extra towels please' : undefined,
    bookingType: pick(BOOKING_TYPES),
    bookingStatus: states.bookingStatus,
    paymentState: states.paymentState,
    mirState: states.mirState,
    stateHistory,
    channel: channel ?? pick(BOOKING_CHANNELS),
    checkInStatus: pick(CHECK_IN_STATUSES),
    locale: pick(LOCALES),
    createdDate,
    createdBy: 'system',
    updatedDate,
    updatedBy: 'system',
    rentalId,
    hostId,
    cancellationPolicy: pick(CANCELLATION_POLICIES),
    isRefundable: randomBool(0.7),
    averageNightlyPrice: avgPrice,
    dailyBreakDown,
    totalBeforeAdjustements: parseFloat(totalBeforeAdj.toFixed(2)),
    maintenance: randomBool(0.2)
      ? { amount: randomFloat(20, 100, 2), description: 'Cleaning fee' }
      : undefined,
    discount: randomBool(0.3)
      ? { amount: randomFloat(10, 50, 2), description: 'Early bird discount' }
      : undefined,
    taxes: { amount: taxAmount, description: 'Tourist tax' },
    totalPrice,
    priceBreakDownCurrency: currency,
    payments: randomBool(0.7) ? [buildPayment(totalPrice, currency)] : [],
    refunds: [],
    bookingHolder: buildHolder(),
    mainGuest: buildMainGuest(),
    rentalTitle: undefined,
    time,
    fullPhone: buildPhone(),
    defaultPaymentGateway: pick(PAYMENT_GATEWAYS),
  };
}

function fixedJanuaryDate(day: number): string {
  return `2026-01-${String(day).padStart(2, '0')}`;
}

function buildBookingStatusHistoryExamples(rentalId: string, hostId: string): Booking[] {
  return BOOKING_STATUSES.map((status, index) =>
    buildBookingWithFixedDates(
      rentalId,
      hostId,
      fixedJanuaryDate(index + 1),
      fixedJanuaryDate(index + 3),
      status
    )
  );
}

/**
 * Shape for injecting fully-custom bookings into buildMockBookingsForTesting.
 * Dates must be YYYY-MM-DD strings.
 */
export interface MockBookingSpec {
  rentalId: string;
  checkIn: string;
  checkOut: string;
  status: string;
  /** Origin channel as the backend sends it, e.g. 'BDC' for a Booking.com reservation. */
  channel?: string;
}

/**
 * Fuente única de todos los bookings mock del sistema.
 *
 * Todas las fechas son RELATIVAS a HOY (día 0) usando daysFromNow().
 * Esto garantiza que los datos mock siempre tengan sentido temporal
 * independientemente del día en que se ejecute la aplicación.
 *
 * Se pueden añadir reservas concretas mediante el parámetro opcional
 * `customSpecs` (array de MockBookingSpec con fechas YYYY-MM-DD fijas).
 *
 * Escenarios automáticos del día de hoy:
 *   r0 → CHECKOUT_PENDING (huésped sale HOY, entró hace 7 días)
 *   r1 → CHECKIN_PENDING (huésped llega MAÑANA, día +1)
 *   r2 → gap (próxima reserva en +5 días)
 *   r3 → CHECKOUT_PENDING (huésped sale HOY, entró hace 4 días)
 *
 * Pasado: 12 reservas FINISHED por alojamiento (~4 meses atrás).
 */
export function buildMockBookingsForTesting(
  rentalIds: string[],
  hostId: string,
  customSpecs?: MockBookingSpec[]
): Booking[] {
  if (rentalIds.length === 0) {
    return [];
  }

  const r0 = rentalIds[0];
  const r1 = rentalIds[1] ?? rentalIds[0];
  const r2 = rentalIds[2] ?? rentalIds[0];
  const r3 = rentalIds[3] ?? rentalIds[0];

  const b = (rentalId: string, checkIn: number, checkOut: number, status: string) =>
    buildBookingWithFixedDates(
      rentalId,
      hostId,
      daysFromNow(checkIn),
      daysFromNow(checkOut),
      status
    );

  const generated: Booking[] = [
    b(r0, -127, -120, 'FINISHED'),
    b(r0, -117, -110, 'FINISHED'),
    b(r0, -107, -100, 'FINISHED'),
    b(r0, -97, -90, 'FINISHED'),
    b(r0, -87, -80, 'FINISHED'),
    b(r0, -77, -70, 'FINISHED'),
    b(r0, -67, -60, 'FINISHED'),
    b(r0, -57, -50, 'FINISHED'),
    b(r0, -47, -40, 'FINISHED'),
    b(r0, -37, -30, 'FINISHED'),
    b(r0, -27, -20, 'FINISHED'),
    b(r0, -17, -10, 'FINISHED'),
    b(r0, -7, 0, 'CHECKOUT_PENDING'),
    b(r0, 3, 10, 'CHECKIN_PENDING'),
    {
      ...b(r0, 13, 20, 'CHECKIN_SCHEDULED'),
      paymentState: 'PAYMENT_PAID',
      mirState: 'SEND_GUEST_MIR_DATA_PENDING',
      channel: 'SSR',
    },
    b(r0, 23, 30, 'BOOKING_NOTIFICATION_PENDING'),
    b(r0, 43, 50, 'ACCEPTANCE_PENDING'),
    b(r0, 53, 60, 'ACCEPTANCE_PENDING'),
    b(r0, 70, 77, 'PAYMENT_PENDING'),

    b(r1, -119, -112, 'FINISHED'),
    b(r1, -109, -102, 'FINISHED'),
    b(r1, -99, -92, 'FINISHED'),
    b(r1, -89, -82, 'FINISHED'),
    b(r1, -79, -72, 'FINISHED'),
    b(r1, -69, -62, 'FINISHED'),
    b(r1, -59, -52, 'FINISHED'),
    b(r1, -49, -42, 'FINISHED'),
    b(r1, -39, -32, 'FINISHED'),
    b(r1, -29, -22, 'FINISHED'),
    b(r1, -19, -12, 'FINISHED'),
    b(r1, -9, -2, 'FINISHED'),
    b(r1, 1, 8, 'CHECKIN_PENDING'),
    b(r1, 11, 18, 'BOOKING_NOTIFICATION_PENDING'),
    b(r1, 21, 28, 'BOOKING_NOTIFICATION_PENDING'),
    b(r1, 31, 38, 'BOOKING_NOTIFICATION_PENDING'),
    b(r1, 51, 58, 'ACCEPTANCE_PENDING'),
    b(r1, 61, 68, 'ACCEPTANCE_PENDING'),
    b(r1, 78, 85, 'PAYMENT_PENDING'),

    b(r2, -118, -111, 'FINISHED'),
    b(r2, -108, -101, 'FINISHED'),
    b(r2, -98, -91, 'FINISHED'),
    b(r2, -88, -81, 'FINISHED'),
    b(r2, -78, -71, 'FINISHED'),
    b(r2, -68, -61, 'FINISHED'),
    b(r2, -58, -51, 'FINISHED'),
    b(r2, -48, -41, 'FINISHED'),
    b(r2, -38, -31, 'FINISHED'),
    b(r2, -28, -21, 'FINISHED'),
    b(r2, -18, -11, 'FINISHED'),
    b(r2, -8, -1, 'FINISHED'),
    b(r2, 5, 12, 'BOOKING_NOTIFICATION_PENDING'),
    b(r2, 15, 22, 'BOOKING_NOTIFICATION_PENDING'),
    b(r2, 25, 32, 'BOOKING_NOTIFICATION_PENDING'),
    b(r2, 45, 52, 'ACCEPTANCE_PENDING'),
    b(r2, 55, 62, 'ACCEPTANCE_PENDING'),
    b(r2, 75, 82, 'PAYMENT_PENDING'),

    b(r3, -124, -117, 'FINISHED'),
    b(r3, -114, -107, 'FINISHED'),
    b(r3, -104, -97, 'FINISHED'),
    b(r3, -94, -87, 'FINISHED'),
    b(r3, -84, -77, 'FINISHED'),
    b(r3, -74, -67, 'FINISHED'),
    b(r3, -64, -57, 'FINISHED'),
    b(r3, -54, -47, 'FINISHED'),
    b(r3, -44, -37, 'FINISHED'),
    b(r3, -34, -27, 'FINISHED'),
    b(r3, -24, -17, 'FINISHED'),
    b(r3, -14, -7, 'FINISHED'),
    b(r3, -4, 0, 'CHECKOUT_PENDING'),
    b(r3, 3, 10, 'CHECKIN_PENDING'),
    b(r3, 13, 20, 'BOOKING_NOTIFICATION_PENDING'),
    b(r3, 23, 30, 'BOOKING_NOTIFICATION_PENDING'),
    b(r3, 43, 50, 'ACCEPTANCE_PENDING'),
    b(r3, 53, 60, 'ACCEPTANCE_PENDING'),
    b(r3, 70, 77, 'PAYMENT_PENDING'),
  ];

  if (customSpecs?.length) {
    generated.push(
      ...customSpecs.map(spec =>
        buildBookingWithFixedDates(
          spec.rentalId,
          hostId,
          spec.checkIn,
          spec.checkOut,
          spec.status,
          spec.channel
        )
      )
    );
  }

  generated.push(...buildBookingStatusHistoryExamples(r0, hostId));

  return generated;
}
